import { ResponsiveContainer } from 'recharts';

import ArrowDownIcon from '@/assets/svg/ui-elements/arrow-down.svg';
import ArrowUpIcon from '@/assets/svg/ui-elements/arrow-up.svg';
import { Badge, Spinner, theme, Typography } from '@/design-system';

import { ChartPeriodType } from '../../common';
import { PerformanceChart } from './performance-chart';
import {
  BalanceBlock,
  BalanceRow,
  BalanceValue,
  ChartCard,
  ChartHeader,
  Plot,
  PlotPlaceholder,
} from './performance-chart-card.styled';
import { PerformancePeriodSelector } from './performance-period-selector';

interface PerformanceChartCardProps {
  isMobile: boolean;
  balance: { integer: string; fraction: string };
  balanceDisplayValue: string;
  performanceChange?: { percent: string; isPositive: boolean };
  performanceData: Array<{ date: string; value: number }>;
  hasChartData: boolean;
  isLoading: boolean;
  activePeriod: ChartPeriodType;
  onPeriodChange: (_period: ChartPeriodType) => void;
}

export const PerformanceChartCard = ({
  isMobile,
  balance,
  balanceDisplayValue,
  performanceChange,
  performanceData,
  hasChartData,
  isLoading,
  activePeriod,
  onPeriodChange,
}: PerformanceChartCardProps) => (
  <ChartCard aria-label="Portfolio performance chart">
    <ChartHeader>
      <BalanceBlock>
        <Typography variant={isMobile ? 'body3' : 'body2'} color={theme.colors.text.secondary}>
          Total Balance
        </Typography>
        <BalanceRow>
          <BalanceValue aria-label={`Total balance: $${balanceDisplayValue}`}>
            <Typography variant="h4" color={theme.colors.text.secondary}>
              $
            </Typography>
            <Typography variant="h4" color={theme.colors.text.primary}>
              {balance.integer}
            </Typography>
            <Typography variant="body1" fontWeight={600} color={theme.colors.text.secondary}>
              {balance.fraction}
            </Typography>
          </BalanceValue>
          {performanceChange && (
            <Badge
              label={performanceChange.percent}
              color={performanceChange.isPositive ? 'primary' : 'destructive'}
              variant="subtle"
              size="sm"
              startIcon={performanceChange.isPositive ? <ArrowUpIcon /> : <ArrowDownIcon />}
            />
          )}
        </BalanceRow>
      </BalanceBlock>

      {!isMobile && (
        <PerformancePeriodSelector activePeriod={activePeriod} onPeriodChange={onPeriodChange} />
      )}
    </ChartHeader>

    {isMobile && (
      <PerformancePeriodSelector activePeriod={activePeriod} onPeriodChange={onPeriodChange} />
    )}

    <Plot>
      {isLoading ? (
        <PlotPlaceholder>
          <Spinner />
        </PlotPlaceholder>
      ) : hasChartData ? (
        <ResponsiveContainer width="100%" height="100%">
          <PerformanceChart
            data={performanceData}
            width={100}
            height={220}
            format="$"
            lineWidth={1}
          />
        </ResponsiveContainer>
      ) : (
        <PlotPlaceholder>
          <Typography variant="body3" color={theme.colors.text.secondary}>
            No performance data yet
          </Typography>
        </PlotPlaceholder>
      )}
    </Plot>
  </ChartCard>
);
